import React from 'react';
import { BsCheck, BsFileEarmarkMedicalFill, BsFlagFill, BsX } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import {
  Box, Divider, Flex, Grid, GridItem, HStack, Icon, Image, Popover,
  PopoverArrow,
  PopoverBody, PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text, Tooltip
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import checkMark from '../../../../../../../../ai_checkmark.png';
import xMark from '../../../../../../../../ai_x.png';
import TruncatableText from '../../../../../../../../components/TruncatableText';
import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { FailedGptRequestStatuses } from '../../../../../../../../constants/gptRequestStatuses';
import { generateS3GetUrl } from '../../../../../../../../redux/modules/documents';
import { useMarkQuestionAsRead } from '../../../../../../../../services/question-service';
import { useRecordQuestionRequirements } from '../../../../../../../../services/record-service';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import { getDisplayValueForType } from '../../../../../../../../utils/tableUtil';

import FailedScanRetry from './FailedScanRetry';
import NoAnswerCell from './NoAnswerCell';
import ResourceDocumentButton from './ResourceDocumentButton';

const mostRecentAnswerIcon = (redFlag, answer) => {
  if (answer === 1) {
    return {
      icon: redFlag ? BsFlagFill : BsCheck,
      image: redFlag ? xMark : checkMark,
      color: redFlag ? 'error.primary.600' : 'success.primary.600',
      label: redFlag ? 'The answer is yes, but this question was set to throw a red flag.' : '',
      iconSize: redFlag ? 6 : 8
    };
  }

  if (answer === 0) {
    return {
      icon: redFlag ? BsCheck : BsX,
      image: redFlag ? checkMark : xMark,
      color: redFlag ? 'success.primary.600' : 'error.primary.600',
      label: redFlag ? 'The answer is no, but this question was set to throw a red flag.' : '',
      iconSize: 8
    };
  }

  return null;
};

const QuestionRequirementTable = ({ recordId, gptStatus, relatedQuestions }) => {
  const dispatch = useDispatch();
  const { mutate: markQuestionAsRead } = useMarkQuestionAsRead({ recordId });
  const { data: questions } = useRecordQuestionRequirements(recordId);

  const handleDocClick = async docId => {
    const getUrl = await dispatch(generateS3GetUrl(docId));
    window.open(getUrl, '_blank');
  };

  const handleMarkedAsReadClick = questionAnswerId => {
    markQuestionAsRead({ questionAnswerId });
  };

  if (FailedGptRequestStatuses.includes(gptStatus)) {
    return <FailedScanRetry gptStatus={gptStatus} recordId={recordId} />;
  }

  return (
    <>
      <Box bg={colors.greyScale['75']} px={2}>
        <Grid
          bg="inherit"
          color={colors.greyScale['800']}
          gap={4}
          gridTemplateColumns="repeat(12, 1fr);"
          h="44px"
          position="sticky"
          py={2}
          rowGap={4}
          textStyle="bodySm500"
          top="initial"
          width="100%"
          zIndex="initial"
        >
          <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2}>Question</GridItem>
          <GridItem alignSelf="center" colSpan={1} justifySelf="center" mr={2}>Answer</GridItem>
          <GridItem alignSelf="center" colSpan={5} justifySelf="left" mr={2}>Reasoning</GridItem>
          <GridItem alignSelf="center" colSpan={2} justifySelf="center" mr={1}>Date</GridItem>
          <GridItem alignSelf="center" colSpan={1} justifySelf="center" mr={2} w="150px" />
        </Grid>
      </Box>

      {questions.length > 0
        ? (
          <Box maxH="calc(100vh - 400px)" overflowX="auto" overflowY="auto">
            {relatedQuestions.map(q => {
              const {
                question: { id: questionId, name: questionName, question: questionDescription, redFlag },
                answers
              } = q;

              const publishedAnswers = answers.filter(a => a.publishedAt);
              const mostRecentAnswer = publishedAnswers[publishedAnswers.length - 1];
              const {
                icon: answerIcon,
                image: answerImage,
                color: answerColor,
                label: answerLabel,
                iconSize
              } = mostRecentAnswerIcon(redFlag, mostRecentAnswer?.answer) ?? {};
              const answerReason = mostRecentAnswer?.reason;
              const answerDate = mostRecentAnswer?.createdAt;
              const isRead = !!mostRecentAnswer?.viewedAt;

              return (
                <Box
                  data-cy={CypressIds.questionRequirementRow(questionId)}
                  key={q.id}
                  px={2}
                >
                  <Grid
                    bg="inherit"
                    color={colors.greyScale['900']}
                    gap={4}
                    gridTemplateColumns="repeat(12, 1fr);"
                    minH="100px"
                    position="sticky"
                    py={4}
                    rowGap={4}
                    textStyle="bodyMd"
                    width="100%"
                    zIndex="initial"
                  >
                    <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2}>
                      <Tooltip label={questionName}>
                        <Text
                          color={colors.greyScale['900']}
                          data-cy={CypressIds.questionRequirementQuestion(questionId)}
                          textAlign="left"
                          textStyle="bodyMd600"
                        >
                          {questionDescription}
                        </Text>
                      </Tooltip>
                    </GridItem>

                    <GridItem
                      alignSelf="center"
                      colSpan={1}
                      data-answer={mostRecentAnswer?.answer === 1}
                      data-cy={CypressIds.questionRequirementAnswer(questionId)}
                      justifySelf="center"
                      mr={2}
                    >
                      {
                    answerIcon
                      ? (
                        <Tooltip shouldWrapChildren label={answerLabel}>
                          <Image
                            _hover={{ boxSize: '50px' }}
                            alt="answer icon"
                            boxSize="44px"
                            src={answerImage}
                            transition="all 0.2s ease-in-out"
                          />
                        </Tooltip>
                      )
                      : (<NoAnswerCell />)
                  }
                    </GridItem>

                    <GridItem
                      alignSelf="center"
                      colSpan={5}
                      data-cy={CypressIds.questionRequirementReason(questionId)}
                      justifySelf="left"
                      mr={2}
                    >
                      {answerReason ? (
                        <>
                          <TruncatableText
                            limit={320}
                            text={answerReason}
                            textAlign="left"
                          />
                          <ResourceDocumentButton
                            handleDocClick={handleDocClick}
                            links={mostRecentAnswer?.gptRequest?.links}
                          />
                        </>
                      ) : <NoAnswerCell />}
                    </GridItem>

                    <GridItem
                      alignSelf="center"
                      colSpan={2}
                      data-cy={CypressIds.questionRequirementDate(questionId)}
                      justifySelf="center"
                      mr={1}
                    >
                      {answerDate
                        ? <Text color={colors.greyScale['900']} textAlign="center">{getDisplayValueForType(answerDate, 'datetime')}</Text>
                        : <NoAnswerCell />}
                    </GridItem>

                    <GridItem
                      alignSelf="center"
                      colSpan={1}
                      data-cy={CypressIds.questionRequirementMarkAsEmptyIcon(questionId)}
                      data-opened={isRead}
                      justifySelf="center"
                      mr={2}
                      w="150px"
                      onClick={() => handleMarkedAsReadClick(mostRecentAnswer.id)}
                    >
                      {mostRecentAnswer
                        ? (
                          <Text
                            as="i"
                            color={colors.greyScale['500']}
                            cursor="pointer"
                            textStyle="bodyMd"
                          >
                            {isRead ? 'Read' : 'Unread'}
                          </Text>
                        )
                        : (
                          <div />
                        )}
                    </GridItem>
                  </Grid>
                </Box>
              );
            })}
          </Box>
        )
        : (
          <Box w="100%">
            <Text
              color="greyScale.500"
              textAlign="center"
              textStyle="bodyMd600"
            >
              No scans completed yet for this requirement.
            </Text>
          </Box>
        )}
    </>
  );
};

QuestionRequirementTable.propTypes = {
  recordId: PropTypes.number.isRequired,
  gptStatus: PropTypes.string.isRequired,
  relatedQuestions: PropTypes.arrayOf(PropTypes.shape({
    question: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      question: PropTypes.string.isRequired,
      redFlag: PropTypes.bool.isRequired
    }),
    answers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      answer: PropTypes.number.isRequired
    }))
  }))
};

export default QuestionRequirementTable;
